import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, X, ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { toast } from '../hooks/use-toast';

const Wishlist = () => {
  const { wishlist, removeFromWishlist, addToCart } = useCart();

  const handleAddToCart = (product) => {
    addToCart(product);
    toast({
      title: 'Added to cart',
      description: `${product.name} has been added to your cart.`
    });
  };

  if (wishlist.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Heart size={64} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your wishlist is empty</h2>
          <p className="text-gray-600 mb-6">Save your favorite products for later!</p>
          <Link
            to="/products"
            className="inline-block bg-red-600 text-white px-8 py-3 rounded hover:bg-red-700 transition"
          >
            Explore Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">My Wishlist</h1>

        <div className="bg-white rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Product</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Price</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Stock Status</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Action</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Remove</th>
                </tr>
              </thead>
              <tbody>
                {wishlist.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Link to={`/product/${item.slug}`} className="flex-shrink-0">
                          <img
                            src={item.image || '/placeholder.jpg'}
                            alt={item.name}
                            onError={(e) => {
                              e.target.src = '/placeholder.jpg';
                            }}
                            className="w-20 h-20 object-cover rounded"
                          />
                        </Link>
                        <div className="ml-4">
                          <Link
                            to={`/product/${item.slug}`}
                            className="font-semibold text-gray-900 hover:text-red-600 transition"
                          >
                            {item.name}
                          </Link>
                          <p className="text-sm text-gray-500">SKU: {item.sku}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-bold text-gray-900">${item.price.toFixed(2)}</p>
                        {item.originalPrice && (
                          <p className="text-sm text-gray-400 line-through">
                            ${item.originalPrice.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {item.stock > 0 ? (
                        <span className="inline-flex items-center text-green-600 font-medium text-sm">
                          <span className="w-2 h-2 bg-green-600 rounded-full mr-2"></span>
                          In Stock
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-red-600 font-medium text-sm">
                          <span className="w-2 h-2 bg-red-600 rounded-full mr-2"></span>
                          Out of Stock
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleAddToCart(item)}
                        disabled={item.stock === 0}
                        className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed inline-flex items-center space-x-2"
                      >
                        <ShoppingCart size={16} />
                        <span>Add to Cart</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => removeFromWishlist(item.id)}
                        className="text-red-600 hover:text-red-700 transition"
                      >
                        <X size={20} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Continue Shopping */}
        <div className="mt-6">
          <Link
            to="/products"
            className="inline-flex items-center text-red-600 hover:text-red-700 font-semibold"
          >
            ← Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
